import React, { useState, useEffect } from 'react'
import { Routes, Route, useNavigate, useParams, useLocation, Navigate, Link } from 'react-router-dom'
import TableView from './components/TableView'
import CalendarView from './components/CalendarView'
import MiniCalendar from './components/MiniCalendar'
import SectorSelector from './components/SectorSelector'
import FeedbackModal from './components/FeedbackModal'
import ConferenceModal from './components/ConferenceModal'
import { MessageCircle, ChevronRight } from 'lucide-react'
import { discoverAvailableSectors, loadSectorData as loadSectorDataFromFile, getSampleData, extractSectorFromConferenceId } from './utils/sectorDiscovery'

function AppContent() {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()
  
  // Conference modal state from URL
  const selectedConferenceId = params.conferenceId
  const urlSector = params.sector
  const [selectedConference, setSelectedConference] = useState(null)
  const showModal = !!selectedConferenceId
  
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showFeedback, setShowFeedback] = useState(false)
  
  // Sector management
  const [availableSectors, setAvailableSectors] = useState([])
  const [selectedSector, setSelectedSector] = useState('')
  
  // Filter states - using Sets for multi-select
  const [selectedOrganisations, setSelectedOrganisations] = useState(new Set())
  const [yearFilter, setYearFilter] = useState(new Set())
  const [typeFilter, setTypeFilter] = useState(new Set())
  const [verifiedFilter, setVerifiedFilter] = useState(new Set())
  const [relevanceFilter, setRelevanceFilter] = useState(0)
  const [locationFilter, setLocationFilter] = useState(new Set())
  const [costFilter, setCostFilter] = useState(new Set())
  const [registrationDeadlineFilter, setRegistrationDeadlineFilter] = useState(new Set())
  const [submissionDeadlineFilter, setSubmissionDeadlineFilter] = useState(new Set())
  const [memberSizeFilter, setMemberSizeFilter] = useState(new Set())

  // Calendar states - get from URL params if available
  const currentDate = new Date()
  const urlYear = params.year ? parseInt(params.year) : currentDate.getFullYear()
  const urlMonth = params.month ? parseInt(params.month) - 1 : currentDate.getMonth()
  const [calendarYear, setCalendarYear] = useState(urlYear)
  const [calendarMonth, setCalendarMonth] = useState(urlMonth)

  // Accordion states - default to expanded on desktop, collapsed on mobile
  const [organisationsExpanded, setOrganisationsExpanded] = useState(window.innerWidth > 768)
  const [conferenceFiltersExpanded, setConferenceFiltersExpanded] = useState(window.innerWidth > 768)

  // Multi-select dropdown states
  const [dropdownStates, setDropdownStates] = useState({})

  // Handle window resize to update accordion states
  useEffect(() => {
    const handleResize = () => {
      const isDesktop = window.innerWidth > 768
      setOrganisationsExpanded(isDesktop)
      setConferenceFiltersExpanded(isDesktop)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    initializeApp()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedSector) {
      loadSectorDataForApp(selectedSector)
    }
  }, [selectedSector])

  // Update calendar state when URL params change
  useEffect(() => {
    if (params.year && params.month) {
      setCalendarYear(parseInt(params.year))
      setCalendarMonth(parseInt(params.month) - 1)
    }
  }, [params.year, params.month])

  // Update selected conference when URL changes
  useEffect(() => {
    if (selectedConferenceId && data?.conferences && selectedSector) {
      // First try to find by nameUrlFormatted, then fallback to ID-based lookup for backwards compatibility
      let conference = data.conferences.find(c => c.nameUrlFormatted === selectedConferenceId)
      
      if (!conference) {
        // Fallback: reconstruct full conference ID with sector prefix for backwards compatibility
        const fullConferenceId = selectedConferenceId.startsWith(selectedSector + '_') 
          ? selectedConferenceId 
          : `${selectedSector}_${selectedConferenceId}`
        conference = data.conferences.find(c => c.id === fullConferenceId)
      }
      
      setSelectedConference(conference || null)
    } else {
      setSelectedConference(null)
    }
  }, [selectedConferenceId, data?.conferences, selectedSector])

  const initializeApp = async () => {
    try {
      setLoading(true)
      
      // Discover available sectors
      const sectors = await discoverAvailableSectors()
      setAvailableSectors(sectors)
      
      if (sectors.length > 0) {
        // Check if we have a sector in the URL
        let sectorToLoad = null
        
        if (urlSector && sectors.find(s => s.id === urlSector)) {
          sectorToLoad = urlSector
        } else if (selectedConferenceId) {
          // Fallback: extract sector from conference ID
          const extractedSector = extractSectorFromConferenceId(selectedConferenceId)
          if (extractedSector && sectors.find(s => s.id === extractedSector)) {
            sectorToLoad = extractedSector
          }
        }
        
        // Auto-select first available sector if no sector from URL
        if (!sectorToLoad) {
          sectorToLoad = sectors[0].id
          // Redirect to include sector in URL
          const currentPath = location.pathname
          if (!currentPath.includes(`/${sectorToLoad}/`)) {
            if (currentPath === '/' || currentPath === '/table') {
              navigate(`/${sectorToLoad}/table`, { replace: true })
            } else if (currentPath.includes('/calendar')) {
              navigate(`/${sectorToLoad}/calendar/${calendarYear}/${calendarMonth + 1}`, { replace: true })
            }
          }
        }
        
        setSelectedSector(sectorToLoad)
      } else {
        // No sectors found, show sample data
        setData(getSampleData('sample'))
        setSelectedOrganisations(new Set(['sample_org_001', 'sample_org_002', 'sample_org_003']))
        setLoading(false)
      }
      
    } catch (err) {
      console.error('Error initializing app:', err)
      setError('Failed to load available sectors')
      setData(getSampleData('sample'))
      setLoading(false)
    }
  }

  const loadSectorDataForApp = async (sectorId) => {
    try {
      setLoading(true)
      setError(null)
      
      const result = await loadSectorDataFromFile(sectorId)
      setData(result)
      
      // Initialize all organisations as selected
      if (result.organisations) {
        setSelectedOrganisations(new Set(result.organisations.map(org => org.id)))
      }
      
    } catch (err) {
      console.error('Error loading sector data:', err)
      setError(`Failed to load data for sector: ${sectorId}`)
      setData(getSampleData(sectorId))
    } finally {
      setLoading(false)
    }
  }

  const handleSectorChange = (newSectorId) => {
    setSelectedSector(newSectorId)
    // Reset filters when switching sectors
    setYearFilter(new Set())
    setTypeFilter(new Set())
    setVerifiedFilter(new Set())
    setRelevanceFilter(0)
    setLocationFilter(new Set())
    setCostFilter(new Set())
    setRegistrationDeadlineFilter(new Set())
    setSubmissionDeadlineFilter(new Set())
    setMemberSizeFilter(new Set())
    
    // Navigate to new sector URL
    const currentView = getCurrentView()
    if (currentView === 'table') {
      navigate(`/${newSectorId}/table`)
    } else if (currentView === 'calendar') {
      navigate(`/${newSectorId}/calendar/${calendarYear}/${calendarMonth + 1}`)
    }
  }


  // Multi-select component
  const MultiSelectFilter = React.memo(function MultiSelectFilter({ label, options, selectedValues, onChange, placeholder, dropdownId }) {
    const containerRef = React.useRef(null)
    const isOpen = dropdownStates[dropdownId] || false
    
    const setIsOpen = (open) => {
      setDropdownStates(prev => ({
        ...prev,
        [dropdownId]: open
      }))
    }
    
    const handleOptionToggle = (value) => {
      const newSet = new Set(selectedValues)
      if (newSet.has(value)) {
        newSet.delete(value)
      } else {
        newSet.add(value)
      }
      onChange(newSet)
      // Deliberately NOT closing the dropdown here to keep it open
    }

    const handleClickOutside = (e) => {
      // Check if click is outside the entire multi-select component
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false)
      }
    }

    // Close dropdown when clicking outside
    React.useEffect(() => {
      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [isOpen, handleClickOutside])
    
    return (
      <div className="filter-group">
        <label>{label}</label>
        <div className="multi-select-container" ref={containerRef}>
          <div 
            className="multi-select-trigger" 
            onClick={() => setIsOpen(!isOpen)}
          >
            {selectedValues.size === 0 ? (
              <span className="placeholder">{placeholder || 'Select options...'}</span>
            ) : (
              <span className="selected-count">
                {selectedValues.size} selected
              </span>
            )}
            <span className="dropdown-arrow">{isOpen ? '▲' : '▼'}</span>
          </div>
          {isOpen && (
            <div className="multi-select-dropdown">
              <div className="multi-select-header">
              <button 
                  className="select-all-btn"
                  onClick={(e) => {
                    e.stopPropagation()
                    const allValues = new Set(options.map(option => option.value))
                    onChange(allValues)
                  }}
                >
                  Select All
                </button>
                <button 
                  className="clear-all-btn"
                  onClick={(e) => {
                    e.stopPropagation()
                    onChange(new Set())
                  }}
                >
                  Clear
                </button>
              </div>
              {options.map(option => (
                <div 
                  key={option.value} 
                  className="multi-select-option"
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    handleOptionToggle(option.value)
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedValues.has(option.value)}
                    readOnly
                    style={{ pointerEvents: 'none' }}
                  />
                  <span>{option.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  })


  if (loading) {
    return (
      <div className="App">
        <header className="header">
          <div className="container">
            <h1>Industry Calendar</h1>
            <p>Professional conference tracker</p>
          </div>
        </header>
        <main className="container">
          <div className="view-section">
            <div className="loading-overlay">
              <div className="loading-spinner"></div>
              <h2>Loading conference data...</h2>
              <p>Please wait while we fetch the latest information.</p>
            </div>
          </div>
        </main>
      </div>
    )
  }

  if (error && !data) {
    return (
      <div className="empty-state">
        <h2>Error Loading Data</h2>
        <p>{error}</p>
        <button onClick={initializeApp} className="btn btn-primary">Try Again</button>
      </div>
    )
  }

  // Get current view from URL
  const getCurrentView = () => {
    const path = location.pathname
    if (path.includes('/calendar')) return 'calendar'
    if (path.includes('/table')) return 'table'
    return 'table' // default
  }

  const currentView = getCurrentView()

  // Handle view changes with navigation
  const handleViewChange = (newView) => {
    if (newView === 'table') {
      navigate(`/${selectedSector}/table`)
    } else if (newView === 'calendar') {
      navigate(`/${selectedSector}/calendar/${calendarYear}/${calendarMonth + 1}`)
    }
  }

  // Handle calendar date changes with navigation
  const handleCalendarYearChange = (newYear) => {
    setCalendarYear(newYear)
    if (currentView === 'calendar') {
      navigate(`/${selectedSector}/calendar/${newYear}/${calendarMonth + 1}`)
    }
  }

  const handleCalendarMonthChange = (newMonth) => {
    setCalendarMonth(newMonth)
    if (currentView === 'calendar') {
      navigate(`/${selectedSector}/calendar/${calendarYear}/${newMonth + 1}`)
    }
  }

  const handleCalendarDateChange = (newYear, newMonth) => {
    setCalendarYear(newYear)
    setCalendarMonth(newMonth)
    if (currentView === 'calendar') {
      navigate(`/${selectedSector}/calendar/${newYear}/${newMonth + 1}`)
    }
  }

  // Handle conference modal
  const handleConferenceClick = (conference) => {
    // Use nameUrlFormatted for URL instead of ID
    const conferenceUrlName = conference.nameUrlFormatted || conference.id.replace(`${selectedSector}_`, '')
    if (currentView === 'table') {
      navigate(`/${selectedSector}/table/conference/${conferenceUrlName}`)
    } else if (currentView === 'calendar') {
      navigate(`/${selectedSector}/calendar/${calendarYear}/${calendarMonth + 1}/conference/${conferenceUrlName}`)
    }
  }

  const handleCloseModal = () => {
    if (currentView === 'table') {
      navigate(`/${selectedSector}/table`)
    } else if (currentView === 'calendar') {
      navigate(`/${selectedSector}/calendar/${calendarYear}/${calendarMonth + 1}`)
    }
  }

  return (
    <div className="App">
      <header className="header">
        <div className="container">
          <div className="header-content">
            <div className="header-text">
              <h1>Industry Calendar</h1>
              <p>Professional conference tracker for {data?.metadata?.sector?.replace('-', ' ') || 'various sectors'}</p>
              {data?.metadata?.lastUpdated && (
                <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
                  Last updated: {new Date(data.metadata.lastUpdated).toLocaleDateString()}
                </p>
              )}
            </div>
            <div className="header-controls">
              <SectorSelector
                availableSectors={availableSectors}
                selectedSector={selectedSector}
                onSectorChange={handleSectorChange}
                loading={loading}
              />
            </div>
          </div>
        </div>
      </header>

      <main className="container">
        <div className={`content-layout ${currentView === 'table' ? 'table-view' : 'calendar-view'}`}>
          <div className="view-toggle">
            <Link 
              to={`/${selectedSector}/table`}
              className={currentView === 'table' ? 'active' : ''}
            >
              Table View
            </Link>
            <Link 
              to={`/${selectedSector}/calendar/${calendarYear}/${calendarMonth + 1}`}
              className={currentView === 'calendar' ? 'active' : ''}
            >
              Calendar View
            </Link>
            <Link 
              className={`${currentView === 'timeline' ? 'active' : ''} disabled`}
              onClick={() => handleViewChange('timeline')}
              title="Timeline view coming soon"
            >
              Timeline View (Coming Soon)
            </Link>
          </div>

          <div className="conference-filters">
            <div className="accordion-header" onClick={() => setConferenceFiltersExpanded(!conferenceFiltersExpanded)}>
              <h3>Conference filters</h3>
              <button className={`accordion-toggle ${conferenceFiltersExpanded ? 'open' : ''}`}>
                <ChevronRight size={16} />
              </button>
            </div>
            <div className={`accordion-content ${conferenceFiltersExpanded ? 'expanded' : 'collapsed'}`}>
              <div className="filter-row">
                <MultiSelectFilter
                  label="Year"
                  options={[
                    { value: 'not_specified', label: 'Not specified' },
                    { value: '2024', label: '2024' },
                    { value: '2025', label: '2025' },
                    { value: '2026', label: '2026' },
                    { value: '2027', label: '2027' }
                  ]}
                  selectedValues={yearFilter}
                  onChange={setYearFilter}
                  placeholder="All years"
                  dropdownId="year-filter"
                />

                <MultiSelectFilter
                  label="Location"
                  options={[
                    { value: 'not_specified', label: 'Not specified' },
                    { value: 'auckland', label: 'Auckland' },
                    { value: 'wellington', label: 'Wellington' },
                    { value: 'christchurch', label: 'Christchurch' },
                    { value: 'other_nz', label: 'Other New Zealand' },
                    { value: 'australia', label: 'Australia' },
                    { value: 'international', label: 'Other international' }
                  ]}
                  selectedValues={locationFilter}
                  onChange={setLocationFilter}
                  placeholder="All locations"
                  dropdownId="location-filter"
                />

                <MultiSelectFilter
                  label="Cost"
                  options={[
                    { value: 'not_specified', label: 'Not specified' },
                    { value: 'free', label: 'Free ($0)' },
                    { value: 'low', label: 'Low ($1-$499)' },
                    { value: 'medium', label: 'Medium ($500-$1499)' },
                    { value: 'high', label: 'High ($1500+)' }
                  ]}
                  selectedValues={costFilter}
                  onChange={setCostFilter}
                  placeholder="All costs"
                  dropdownId="cost-filter"
                />

                <MultiSelectFilter
                  label="Registration Deadline"
                  options={[
                    { value: 'not_specified', label: 'Not specified' },
                    { value: 'overdue', label: 'Overdue (past)' },
                    { value: 'soon', label: 'Soon (1-7 days)' },
                    { value: 'upcoming', label: 'Upcoming (8-30 days)' },
                    { value: 'future', label: 'Future (30+ days)' }
                  ]}
                  selectedValues={registrationDeadlineFilter}
                  onChange={setRegistrationDeadlineFilter}
                  placeholder="All deadlines"
                  dropdownId="registration-deadline-filter"
                />

                <MultiSelectFilter
                  label="Submission Deadline"
                  options={[
                    { value: 'not_specified', label: 'Not specified' },
                    { value: 'closed', label: 'Closed (past)' },
                    { value: 'closing_soon', label: 'Closing soon (1-7 days)' },
                    { value: 'open_soon', label: 'Open (8-30 days)' },
                    { value: 'open_future', label: 'Open (30+ days)' }
                  ]}
                  selectedValues={submissionDeadlineFilter}
                  onChange={setSubmissionDeadlineFilter}
                  placeholder="All deadlines"
                  dropdownId="submission-deadline-filter"
                />

                <MultiSelectFilter
                  label="Verification Status"
                  options={[
                    { value: 'verified', label: 'Verified' },
                    { value: 'unverified', label: 'Unverified' }
                  ]}
                  selectedValues={verifiedFilter}
                  onChange={setVerifiedFilter}
                  placeholder="All statuses"
                  dropdownId="verification-filter"
                />
              </div>
            </div>
          </div>

          {(currentView === 'calendar' || currentView === 'timeline') && (
            <MiniCalendar
              data={data}
              selectedYear={calendarYear}
              selectedMonth={calendarMonth}
              onYearChange={handleCalendarYearChange}
              onMonthChange={handleCalendarMonthChange}
            />
          )}

          <div className="view-section">
            {currentView === 'table' && (
              <TableView
                data={data}
                selectedOrganisations={selectedOrganisations}
                yearFilter={yearFilter}
                typeFilter={typeFilter}
                verifiedFilter={verifiedFilter}
                relevanceFilter={relevanceFilter}
                locationFilter={locationFilter}
                costFilter={costFilter}
                registrationDeadlineFilter={registrationDeadlineFilter}
                submissionDeadlineFilter={submissionDeadlineFilter}
                memberSizeFilter={memberSizeFilter}
                onReportIssue={() => setShowFeedback(true)}
                onConferenceClick={handleConferenceClick}
              />
            )}
            {currentView === 'calendar' && (
              <CalendarView
                data={data}
                selectedOrganisations={selectedOrganisations}
                yearFilter={yearFilter}
                typeFilter={typeFilter}
                verifiedFilter={verifiedFilter}
                relevanceFilter={relevanceFilter}
                locationFilter={locationFilter}
                costFilter={costFilter}
                registrationDeadlineFilter={registrationDeadlineFilter}
                submissionDeadlineFilter={submissionDeadlineFilter}
                memberSizeFilter={memberSizeFilter}
                selectedYear={calendarYear}
                selectedMonth={calendarMonth}
                onYearChange={handleCalendarYearChange}
                onMonthChange={handleCalendarMonthChange}
                onDateChange={handleCalendarDateChange}
                onReportIssue={() => setShowFeedback(true)}
                onConferenceClick={handleConferenceClick}
              />
            )}
          </div>

          <div className="organisations-section">
            <div className="accordion-header" onClick={() => setOrganisationsExpanded(!organisationsExpanded)}>
              <h3>Organisation filters</h3>
              <button className={`accordion-toggle ${organisationsExpanded ? 'open' : ''}`}>
                <ChevronRight size={16} />
              </button>
            </div>
            <div className={`accordion-content ${organisationsExpanded ? 'expanded' : 'collapsed'}`}>
              <div className="organisation-filters">                
                <MultiSelectFilter
                  label="Organisation Type"
                  options={[
                    { value: 'not_specified', label: 'Not specified' },
                    { value: 'professional_association', label: 'Professional Association' },
                    { value: 'government', label: 'Government' },
                    { value: 'industry_body', label: 'Industry Body' },
                    { value: 'academic', label: 'Academic' },
                    { value: 'commercial', label: 'Commercial' }
                  ]}
                  selectedValues={typeFilter}
                  onChange={setTypeFilter}
                  placeholder="All types"
                  dropdownId="org-type-filter"
                />

                <MultiSelectFilter
                  label="Number of Members"
                  options={[
                    { value: 'not_specified', label: 'Not specified' },
                    { value: 'small', label: 'Small (1-99)' },
                    { value: 'medium', label: 'Medium (100-999)' },
                    { value: 'large', label: 'Large (1000+)' }
                  ]}
                  selectedValues={memberSizeFilter}
                  onChange={setMemberSizeFilter}
                  placeholder="All sizes"
                  dropdownId="member-size-filter"
                />

                <div className="filter-group">
                  <label>Relevance (minimum %)</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={relevanceFilter}
                    onChange={(e) => setRelevanceFilter(parseInt(e.target.value))}
                  />
                  <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                    {relevanceFilter}%+
                  </span>
                </div>
              </div>

              <div className="organisations-list">
                <div className="multi-select-header">
                  <div className="selection-buttons">
                    <button 
                      className="select-all-btn" 
                      onClick={() => {
                        if (data?.organisations) {
                          setSelectedOrganisations(new Set(data.organisations.map(org => org.id)))
                        }
                      }}
                    >
                      Select All
                    </button>
                    <button 
                      className="clear-all-btn" 
                      onClick={() => setSelectedOrganisations(new Set())}
                    >
                      Clear
                    </button>
                  </div>
                </div>
                
                <div className="checkbox-group">
                  {data?.organisations?.map(org => (
                    <div key={org.id} className="checkbox-item">
                      <input
                        type="checkbox"
                        id={org.id}
                        checked={selectedOrganisations.has(org.id)}
                        onChange={(e) => {
                          const newSelected = new Set(selectedOrganisations)
                          if (e.target.checked) {
                            newSelected.add(org.id)
                          } else {
                            newSelected.delete(org.id)
                          }
                          setSelectedOrganisations(newSelected)
                        }}
                      />
                      <label htmlFor={org.id}>{org.name}</label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <button
        className="feedback-button"
        onClick={() => setShowFeedback(true)}
        title="Report errors or provide feedback"
      >
        <MessageCircle size={24} />
      </button>

      {showFeedback && (
        <FeedbackModal onClose={() => setShowFeedback(false)} />
      )}
      
      {showModal && selectedConference && (
        <ConferenceModal 
          conference={selectedConference} 
          onClose={handleCloseModal}
          onReportIssue={() => setShowFeedback(true)}
        />
      )}
    </div>
  )
}

// Component to handle calendar redirects with sector param
function CalendarRedirect() {
  const { sector } = useParams()
  const currentDate = new Date()
  return <Navigate to={`/${sector}/calendar/${currentDate.getFullYear()}/${currentDate.getMonth() + 1}`} replace />
}

function App() {
  return (
    <Routes>
      <Route path="/" element={<AppContent />} />
      <Route path="/:sector/table" element={<AppContent />} />
      <Route path="/:sector/table/conference/:conferenceId" element={<AppContent />} />
      <Route path="/:sector/calendar" element={<CalendarRedirect />} />
      <Route path="/:sector/calendar/:year/:month" element={<AppContent />} />
      <Route path="/:sector/calendar/:year/:month/conference/:conferenceId" element={<AppContent />} />
      <Route path="*" element={<AppContent />} />
    </Routes>
  )
}

export default App